import React, { useEffect, useMemo, useState } from "react";
import {
  useParams,
  useLocation,
  useNavigate,
} from "react-router-dom";

import SanityFlowRunner from "./SanityFlowRunner";

import {
  fetchSanityLevelByLegacy,
  normalizeLevelKey,
  normalizeQuestKey,
} from "../../lib/sanity/sanityLevels";

import {
  checkLevelAccessForCurrentUser,
} from "../../lib/learning/levelAccess";

import LevelAccessBlockedModal from "../../components/learning/LevelAccessBlockedModal";

const PRECHECK_MAX_AGE_MS = 30 * 1000;

function hasValidPrecheckedAccess({
  state,
  questId,
  subquestId,
  levelKey,
}) {
  if (state?.levelAccessPrechecked !== true) {
    return false;
  }

  const checkedAt = Number(state?.levelAccessCheckedAt || 0);

  if (!checkedAt || Date.now() - checkedAt > PRECHECK_MAX_AGE_MS) {
    return false;
  }

  const checkedQuest = normalizeQuestKey(state?.levelAccessQuestId);
  const checkedSubquest = String(state?.levelAccessSubquestId || "");
  const checkedLevel = normalizeLevelKey(state?.levelAccessLevelKey);

  return (
    checkedQuest === normalizeQuestKey(questId) &&
    checkedSubquest === String(subquestId || "") &&
    checkedLevel === normalizeLevelKey(levelKey)
  );
}

export default function FlowRunner() {
  const {
    questId: pQuest,
    subquestId: pSub,
    levelKey: pLevel,
  } = useParams();

  const location = useLocation();
  const navigate = useNavigate();

  /*
   * The redesign only uses levelKey for content lookup.
   *
   * questId and subquestId are still kept temporarily because:
   * - the current route still contains them
   * - existing access/progress functions still expect them
   *
   * But Sanity content is loaded by Level Key, e.g. L1.
   */
  const questId =
    pQuest ||
    localStorage.getItem("selectedQuestId") ||
    "1";

  const subquestId =
    pSub ||
    localStorage.getItem("selectedSubquestId") ||
    "1-1";

  const levelKey =
    pLevel ||
    localStorage.getItem("selectedLevelKey") ||
    "L1";

  const normalizedQuestId = normalizeQuestKey(questId);
  const normalizedLevelKey = normalizeLevelKey(levelKey);

  const accessPrechecked = useMemo(
    () =>
      hasValidPrecheckedAccess({
        state: location.state,
        questId: normalizedQuestId,
        subquestId,
        levelKey: normalizedLevelKey,
      }),
    [
      location.state,
      normalizedQuestId,
      subquestId,
      normalizedLevelKey,
    ]
  );

  const [sanityLevel, setSanityLevel] = useState(undefined);
  const [loadingSanity, setLoadingSanity] = useState(accessPrechecked);
  const [checkingAccess, setCheckingAccess] = useState(!accessPrechecked);

  const [accessResult, setAccessResult] = useState(
    accessPrechecked
      ? {
          allowed: true,
          reason: "dashboard_prechecked",
        }
      : null
  );

  useEffect(() => {
    if (pQuest) {
      localStorage.setItem("selectedQuestId", pQuest);
    }

    if (pSub) {
      localStorage.setItem("selectedSubquestId", pSub);
    }

    if (pLevel) {
      localStorage.setItem("selectedLevelKey", pLevel);
    }
  }, [pQuest, pSub, pLevel]);

  /*
   * --------------------------------------------------
   * LEVEL ACCESS
   * --------------------------------------------------
   */
  useEffect(() => {
    let active = true;

    if (accessPrechecked) {
      setAccessResult({
        allowed: true,
        reason: "dashboard_prechecked",
      });

      setCheckingAccess(false);

      return () => {
        active = false;
      };
    }

    async function checkAccess() {
      try {
        setCheckingAccess(true);

        const result = await checkLevelAccessForCurrentUser({
          questId: normalizedQuestId,
          subquestId,
          levelKey: normalizedLevelKey,
        });

        if (!active) return;

        setAccessResult(result);
      } catch (error) {
        console.error("Failed to check level access:", error);

        if (!active) return;

        setAccessResult({
          allowed: false,
          reason: "access_check_failed",
          title: "We could not check your progress",
          message:
            error?.message ||
            "Please go to your tasks page, refresh, and try again.",
          buttonLabel: "TASKS",
          tasksPath: "/resources?tab=tasks",
        });
      } finally {
        if (active) {
          setCheckingAccess(false);
        }
      }
    }

    checkAccess();

    return () => {
      active = false;
    };
  }, [
    accessPrechecked,
    normalizedQuestId,
    subquestId,
    normalizedLevelKey,
  ]);

  /*
   * --------------------------------------------------
   * SANITY LEVEL
   * --------------------------------------------------
   */
  useEffect(() => {
    let active = true;

    if (checkingAccess) {
      return () => {
        active = false;
      };
    }

    if (accessResult?.allowed !== true) {
      setLoadingSanity(false);

      return () => {
        active = false;
      };
    }

    async function loadSanityLevel() {
      try {
        setLoadingSanity(true);

        console.log("[FlowRunner] Loading Sanity level:", {
          questId: normalizedQuestId,
          subquestId,
          levelKey: normalizedLevelKey,
        });

        /*
         * Important:
         * Content lookup should now resolve levelDraft by levelKey.
         * questId/subquestId are only temporary route/access placeholders.
         */
        const level = await fetchSanityLevelByLegacy({
          questId: normalizedQuestId,
          subquestId,
          levelKey: normalizedLevelKey,
        });

        if (!active) return;

        console.log("[FlowRunner] Sanity level result:", {
          found: !!level,
          levelKey: normalizedLevelKey,
          title: level?.title,
          steps: level?.steps?.length || 0,
        });

        setSanityLevel(level || null);
      } catch (error) {
        console.error("Failed to load Sanity level:", error);

        if (!active) return;

        setSanityLevel(null);
      } finally {
        if (active) {
          setLoadingSanity(false);
        }
      }
    }

    loadSanityLevel();

    return () => {
      active = false;
    };
  }, [
    checkingAccess,
    accessResult?.allowed,
    normalizedQuestId,
    subquestId,
    normalizedLevelKey,
  ]);

  if (checkingAccess) {
    return (
      <div className="p-6 font-sans">
        Checking access…
      </div>
    );
  }

  if (accessResult && !accessResult.allowed) {
    return (
      <LevelAccessBlockedModal
        open
        title={accessResult.title}
        message={accessResult.message}
        buttonLabel={accessResult.buttonLabel || "TASKS"}
        onClose={() =>
          navigate("/dashboard", {
            replace: true,
          })
        }
        onTasks={() =>
          navigate(accessResult.tasksPath || "/resources?tab=tasks", {
            replace: true,
          })
        }
      />
    );
  }

  if (loadingSanity) {
    return (
      <div className="p-6 font-sans">
        Loading…
      </div>
    );
  }

  if (sanityLevel) {
    return <SanityFlowRunner level={sanityLevel} />;
  }

  /*
   * Important:
   * Do not fall back to FLOW_REGISTRY anymore.
   * The redesign flow should only use Sanity levelDraft.
   */
  return (
    <div className="p-6 font-sans">
      <h2 className="mb-3 text-lg font-bold">
        Level content not found
      </h2>

      <p className="mb-2">
        The app could not load this level from Sanity.
      </p>

      <p className="mb-2">
        Level: <code>{normalizedLevelKey}</code>
      </p>

      <p className="mb-4 text-sm text-gray-600">
        Make sure the Level Draft is published in Sanity and that the frontend
        is using the correct Sanity dataset.
      </p>

      <button
        type="button"
        onClick={() => navigate("/dashboard", { replace: true })}
        className="rounded-xl bg-[#FF914D] px-4 py-3 font-bold text-white"
      >
        Back to Dashboard
      </button>
    </div>
  );
}